import { FormEvent, useState } from "react";

import { NextPage } from "next";
import Head from "next/head";

import { captureWaitlistSignup } from "@/lib/customerio";

import styles from "./waitlist.module.css";

/**
 * Standalone GlenCoco Dialer waitlist splash page (GLE-8116).
 *
 * Its own full-viewport background and centered card, deliberately NOT wrapped
 * in the marketing Layout. Served at /waitlist (e.g. www.glencoco.com/waitlist).
 *
 * Collects contact info (name, work email, company) and qualification answers
 * (all fields required) and sends everything to Customer.io (see
 * @/lib/customerio).
 */

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const CALL_VOLUME_PER_REP_OPTIONS = ["1-10", "11-50", "51-100", "100+"];

const WaitlistPage: NextPage = () => {
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    const formData = new FormData(event.currentTarget);
    const name = formData.get("name")?.toString().trim() ?? "";
    const email = formData.get("email")?.toString().trim() ?? "";
    const company = formData.get("company")?.toString().trim() ?? "";
    const phone = formData.get("phone")?.toString().trim() ?? "";
    const currentDialer =
      formData.get("current_dialer")?.toString().trim() ?? "";
    const repsDialing = formData.get("reps_dialing")?.toString().trim() ?? "";
    const callVolumePerRep =
      formData.get("call_volume_per_rep")?.toString().trim() ?? "";
    const crmSep = formData.get("crm_sep")?.toString().trim() ?? "";

    if (
      name.length === 0 ||
      !EMAIL_PATTERN.test(email) ||
      company.length === 0 ||
      phone.length === 0 ||
      currentDialer.length === 0 ||
      repsDialing.length === 0 ||
      callVolumePerRep.length === 0 ||
      crmSep.length === 0
    ) {
      setErrorMessage("Please complete all fields.");

      return;
    }

    setErrorMessage(null);
    setIsSubmitting(true);

    try {
      await captureWaitlistSignup({
        name,
        email,
        company,
        phone,
        currentDialer,
        repsDialing,
        callVolumePerRep,
        crmSep,
      });
      setIsSuccess(true);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error("[waitlist] signup capture failed", error);
      setErrorMessage("Something went wrong. Please try again.");
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <Head>
        <title>Join the GlenCoco Dialer Waitlist</title>
        <meta name="viewport" content="width=device-width, initial-scale=1.0" />
        <meta name="robots" content="noindex" />
      </Head>

      <main className={styles.page}>
        <div className={styles.card}>
          <div className={styles.badge}>Early Access</div>
          <h1 className={styles.heading}>Join the GlenCoco Dialer Waitlist</h1>
          <p className={styles.sub}>
            Drop your details below to lock in up to{" "}
            <strong>50% off your first month</strong> when the Dialer launches.
            No payment, no commitment — just early access.
          </p>

          {!isSuccess && (
            <form
              className={styles.form}
              name="waitlist-form"
              onSubmit={handleSubmit}
            >
              <input
                className={styles.input}
                type="text"
                name="name"
                id="name"
                placeholder="Your name"
                required
                autoComplete="name"
              />
              <input
                className={styles.input}
                type="email"
                name="email"
                id="email"
                placeholder="Work email"
                required
                autoComplete="email"
              />
              <input
                className={styles.input}
                type="text"
                name="company"
                id="company"
                placeholder="Company"
                required
                autoComplete="organization"
              />
              <input
                className={styles.input}
                type="tel"
                name="phone"
                id="phone"
                placeholder="Phone number"
                required
                autoComplete="tel"
              />

              <div className={styles.field}>
                <label className={styles.label} htmlFor="current_dialer">
                  What dialer do you use today, and what&apos;s it missing?
                </label>
                <textarea
                  className={styles.textarea}
                  name="current_dialer"
                  id="current_dialer"
                  rows={3}
                  required
                />
              </div>

              <div className={styles.field}>
                <label className={styles.label} htmlFor="reps_dialing">
                  Number of reps dialing
                </label>
                <input
                  className={styles.input}
                  type="text"
                  name="reps_dialing"
                  id="reps_dialing"
                  placeholder="e.g. 15"
                  required
                />
              </div>

              <div className={styles.field}>
                <label className={styles.label} htmlFor="call_volume_per_rep">
                  Call volume per rep
                </label>
                <select
                  className={styles.input}
                  name="call_volume_per_rep"
                  id="call_volume_per_rep"
                  defaultValue=""
                  required
                >
                  <option value="" disabled>
                    Select a range
                  </option>
                  {CALL_VOLUME_PER_REP_OPTIONS.map((range) => (
                    <option key={range} value={range}>
                      {range}
                    </option>
                  ))}
                </select>
              </div>

              <div className={styles.field}>
                <label className={styles.label} htmlFor="crm_sep">
                  CRM / SEP currently used?
                </label>
                <input
                  className={styles.input}
                  type="text"
                  name="crm_sep"
                  id="crm_sep"
                  required
                />
              </div>

              <button
                className={styles.button}
                type="submit"
                disabled={isSubmitting}
              >
                {isSubmitting ? "Submitting..." : "Join the Waitlist"}
              </button>
              {errorMessage && (
                <div className={styles.error}>{errorMessage}</div>
              )}
            </form>
          )}

          {isSuccess && (
            <div className={styles.success}>
              🎉 You&apos;re on the list! We&apos;ll be in touch when it&apos;s
              your turn.
            </div>
          )}

          <div className={styles.footnote}>
            We&apos;ll only use your details to notify you about the Dialer
            launch and your discount.
          </div>
        </div>
      </main>
    </>
  );
};

export default WaitlistPage;
